import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '@material-ui/core';
import Modal, { ModalProps } from '../../../components/Modal';
import ModalActions from '../../../components/ModalActions';
import ModalTitle from '../../../components/ModalTitle';
import TokenInput from '../../../components/TokenInput';
import { getDisplayBalance } from '../../../utils/formatBalance';
import { BigNumber } from 'ethers';
import { ethers } from 'ethers';
import { AutoCompounder } from '../../../tomb-finance';
import useApprove2, { ApprovalState } from '../../../hooks/useApprove2';

interface DepositModalProps extends ModalProps {
  max: BigNumber;
  decimals: number;
  onConfirm: (amount: string) => void;
  tokenName?: string;
  autoCompounder: AutoCompounder;
}

const DepositModal: React.FC<DepositModalProps> = ({ 
  max, 
  decimals, 
  onConfirm, 
  onDismiss, 
  tokenName = '',
  autoCompounder 
}) => {
  const [val, setVal] = useState('');
  const [approveStatus, approve, currentAllowance] = useApprove2(autoCompounder.depositToken, autoCompounder.linkVault);
  const [isApproving, setIsApproving] = React.useState(false);

  const fullBalance = useMemo(() => {
    return getDisplayBalance(max, decimals, 8, false);
  }, [max, decimals]);

  const needsApproval = useMemo(() => {
    if (!currentAllowance || !val) return false;
    const bigAmount = ethers.utils.parseUnits(val, decimals);
    return currentAllowance.lt(bigAmount);
  }, [currentAllowance, val, decimals]);

  const handleChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setVal(e.currentTarget.value);
    },
    [setVal],
  );

  const handleSelectMax = useCallback(() => {
    setVal(fullBalance);
  }, [fullBalance, setVal]);

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      await approve(val);
    } catch (error) {
      console.error('Approval error:', error);
    } finally {
      setIsApproving(false);
    }
  };

  return (
    <Modal>
      <ModalTitle text={`Deposit ${tokenName}`} />
      <TokenInput
        value={val}
        onSelectMax={handleSelectMax}
        onChange={handleChange}
        max={fullBalance}
        symbol={tokenName}
      />
      {needsApproval && (
        <div className="text-sm text-red-500 mt-2">
          Additional approval needed for this amount
        </div>
      )}
      <ModalActions>
        <button 
          className='tracking-normal inline-flex whitespace-nowrap backdrop-blur items-center justify-center gap-x-1 py-2 px-6 rounded-full font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 [&>svg]:stroke-[3px] bg-button/[0.16] hover:bg-button/[0.24] transition focus-visible:ring-button text-body'
          onClick={needsApproval ? handleApprove : () => onConfirm(val)}
          disabled={isApproving}
        >
          {needsApproval 
            ? (isApproving ? 'Approving...' : 'Approve More') 
            : 'Confirm'}
        </button>
      </ModalActions>
    </Modal>
  );
};

export default DepositModal;
